import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../../../styles/accessibility.css';

const RolesAndPermissions = () => {
  const [activeTab, setActiveTab] = useState('roles');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  useEffect(() => {
    document.title = 'Roles & Permissions - Access Management';
  }, []);

  const roles = [
    {
      id: 1,
      name: 'Administrator',
      description: 'Full system access with all permissions',
      users: 5,
      permissions: ['all'],
      color: 'danger',
      icon: '👑'
    },
    {
      id: 2,
      name: 'Production Manager',
      description: 'Manage production operations and analytics',
      users: 8,
      permissions: ['production.read', 'production.write', 'analytics.read', 'reports.create'],
      color: 'primary',
      icon: '🏭'
    },
    {
      id: 3,
      name: 'Quality Control',
      description: 'Quality assurance and compliance monitoring',
      users: 12,
      permissions: ['quality.read', 'quality.write', 'compliance.read', 'audit.create'],
      color: 'success',
      icon: '✓'
    },
    {
      id: 4,
      name: 'Operations Staff',
      description: 'Day-to-day operations and data entry',
      users: 23,
      permissions: ['production.read', 'operations.write', 'dashboard.read'],
      color: 'info',
      icon: '👷'
    },
    {
      id: 5,
      name: 'Auditor',
      description: 'Read-only access for compliance auditing',
      users: 4,
      permissions: ['*.read', 'audit.create', 'reports.export'],
      color: 'warning',
      icon: '📋'
    },
    {
      id: 6,
      name: 'Viewer',
      description: 'Basic read-only access to dashboards',
      users: 15,
      permissions: ['dashboard.read', 'reports.read'],
      color: 'secondary',
      icon: '👁️'
    }
  ];

  const permissions = [
    {
      category: 'Production',
      items: [
        { id: 'prod.view', name: 'View Production Data', description: 'Access production metrics and analytics' },
        { id: 'prod.edit', name: 'Edit Production', description: 'Modify production settings and schedules' },
        { id: 'prod.delete', name: 'Delete Production Records', description: 'Remove production data (restricted)' },
        { id: 'prod.analytics', name: 'Production Analytics', description: 'Access advanced analytics tools' }
      ]
    },
    {
      category: 'Quality Control',
      items: [
        { id: 'qc.view', name: 'View Quality Data', description: 'Access quality metrics and reports' },
        { id: 'qc.inspect', name: 'Perform Inspections', description: 'Conduct quality inspections' },
        { id: 'qc.approve', name: 'Approve Quality', description: 'Approve or reject quality checks' },
        { id: 'qc.standards', name: 'Manage Standards', description: 'Update quality standards' }
      ]
    },
    {
      category: 'Access Management',
      items: [
        { id: 'access.view', name: 'View Users', description: 'View user list and details' },
        { id: 'access.create', name: 'Create Users', description: 'Add new users to the system' },
        { id: 'access.edit', name: 'Edit Users', description: 'Modify user settings and roles' },
        { id: 'access.delete', name: 'Delete Users', description: 'Remove users (admin only)' },
        { id: 'access.roles', name: 'Manage Roles', description: 'Create and modify roles' }
      ]
    },
    {
      category: 'Compliance',
      items: [
        { id: 'comp.view', name: 'View Compliance', description: 'Access compliance dashboards' },
        { id: 'comp.audit', name: 'Create Audits', description: 'Initiate compliance audits' },
        { id: 'comp.policies', name: 'Manage Policies', description: 'Update compliance policies' },
        { id: 'comp.reports', name: 'Generate Reports', description: 'Create compliance reports' }
      ]
    },
    {
      category: 'System',
      items: [
        { id: 'sys.settings', name: 'System Settings', description: 'Access system configuration' },
        { id: 'sys.backup', name: 'Backup & Restore', description: 'Manage system backups' },
        { id: 'sys.logs', name: 'View Logs', description: 'Access system audit logs' },
        { id: 'sys.integration', name: 'Manage Integrations', description: 'Configure external integrations' }
      ]
    }
  ];

  const auditLog = [
    { id: 1, user: 'John Smith', action: 'Created role "Quality Inspector"', time: '10 mins ago', type: 'create' },
    { id: 2, user: 'Sarah Johnson', action: 'Modified permissions for "Operations Staff"', time: '1 hour ago', type: 'modify' },
    { id: 3, user: 'Admin', action: 'Deleted role "Temporary Contractor"', time: '2 hours ago', type: 'delete' },
    { id: 4, user: 'Mike Chen', action: 'Assigned "Auditor" role to Lisa Wang', time: '3 hours ago', type: 'assign' }
  ];

  return (
    <div className="container-fluid py-4">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/bread/access-management">Access Management</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">Roles & Permissions</li>
        </ol>
      </nav>

      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h1 className="display-6 fw-bold mb-2">
                <i className="bi bi-shield-lock me-2 text-primary"></i>
                Roles & Permissions
              </h1>
              <p className="text-muted mb-0">
                Manage user roles, permissions, and access control policies
              </p>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-primary btn-sm">
                <i className="bi bi-download me-1"></i>
                Export
              </button>
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => setShowCreateModal(true)}
              >
                <i className="bi bi-plus-circle me-1"></i>
                Create Role
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'roles' ? 'active' : ''}`}
            onClick={() => setActiveTab('roles')}
          >
            <i className="bi bi-people me-2"></i>
            Roles
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'permissions' ? 'active' : ''}`}
            onClick={() => setActiveTab('permissions')}
          >
            <i className="bi bi-key me-2"></i>
            Permissions
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'audit' ? 'active' : ''}`}
            onClick={() => setActiveTab('audit')}
          >
            <i className="bi bi-clock-history me-2"></i>
            Audit Log
          </button>
        </li>
      </ul>

      {/* Roles Tab */}
      {activeTab === 'roles' && (
        <div className="row g-3">
          {roles.map((role) => (
            <div key={role.id} className="col-12 col-md-6 col-xl-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <span className="fs-3">{role.icon}</span>
                      <div>
                        <h5 className="mb-1">{role.name}</h5>
                        <span className={`badge bg-${role.color}`}>
                          {role.users} {role.users === 1 ? 'user' : 'users'}
                        </span>
                      </div>
                    </div>
                    <div className="dropdown">
                      <button 
                        className="btn btn-sm btn-link text-muted"
                        type="button"
                        data-bs-toggle="dropdown"
                      >
                        <i className="bi bi-three-dots-vertical"></i>
                      </button>
                      <ul className="dropdown-menu">
                        <li>
                          <button className="dropdown-item" onClick={() => setSelectedRole(role)}>
                            <i className="bi bi-pencil me-2"></i>Edit
                          </button>
                        </li>
                        <li>
                          <button className="dropdown-item">
                            <i className="bi bi-files me-2"></i>Duplicate
                          </button>
                        </li>
                        <li><hr className="dropdown-divider" /></li>
                        <li>
                          <button className="dropdown-item text-danger">
                            <i className="bi bi-trash me-2"></i>Delete
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                  
                  <p className="text-muted small mb-3">{role.description}</p>
                  
                  <div className="mb-3">
                    <h6 className="small text-muted mb-2">Permissions:</h6>
                    <div className="d-flex flex-wrap gap-1">
                      {role.permissions.slice(0, 3).map((perm, idx) => (
                        <span key={idx} className="badge bg-light text-dark small">
                          {perm}
                        </span>
                      ))}
                      {role.permissions.length > 3 && (
                        <span className="badge bg-light text-dark small">
                          +{role.permissions.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-outline-primary flex-grow-1">
                      <i className="bi bi-eye me-1"></i>
                      View Details
                    </button>
                    <button className="btn btn-sm btn-outline-secondary">
                      <i className="bi bi-people"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Permissions Tab */}
      {activeTab === 'permissions' && (
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="mb-3">
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="Search permissions..."
                  />
                </div>
                
                {permissions.map((category, idx) => (
                  <div key={idx} className="mb-4">
                    <h5 className="mb-3">
                      <i className="bi bi-folder me-2 text-primary"></i>
                      {category.category}
                    </h5>
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th style={{ width: '30%' }}>Permission</th>
                            <th>Description</th>
                            <th style={{ width: '15%' }}>Roles</th>
                            <th style={{ width: '10%' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {category.items.map((item) => (
                            <tr key={item.id}>
                              <td>
                                <code className="small">{item.id}</code>
                                <div className="fw-medium">{item.name}</div>
                              </td>
                              <td className="text-muted small">{item.description}</td>
                              <td>
                                <span className="badge bg-info me-1">3</span>
                                <small className="text-muted">roles</small>
                              </td>
                              <td>
                                <button className="btn btn-sm btn-outline-primary">
                                  <i className="bi bi-eye"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Audit Log Tab */}
      {activeTab === 'audit' && (
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Recent Activity</h5>
                  <div className="d-flex gap-2">
                    <select className="form-select form-select-sm" style={{ width: 'auto' }}>
                      <option>All Actions</option>
                      <option>Create</option>
                      <option>Modify</option>
                      <option>Delete</option>
                      <option>Assign</option>
                    </select>
                    <select className="form-select form-select-sm" style={{ width: 'auto' }}>
                      <option>Last 7 days</option>
                      <option>Last 30 days</option>
                      <option>Last 90 days</option>
                    </select>
                  </div>
                </div>
                
                <div className="timeline">
                  {auditLog.map((log) => (
                    <div key={log.id} className="d-flex gap-3 mb-3 pb-3 border-bottom">
                      <div className="flex-shrink-0">
                        <div 
                          className={`rounded-circle d-flex align-items-center justify-content-center ${
                            log.type === 'create' ? 'bg-success' :
                            log.type === 'modify' ? 'bg-info' :
                            log.type === 'delete' ? 'bg-danger' :
                            'bg-warning'
                          } bg-opacity-10`}
                          style={{ width: '40px', height: '40px' }}
                        >
                          <i className={`bi ${
                            log.type === 'create' ? 'bi-plus-circle text-success' :
                            log.type === 'modify' ? 'bi-pencil text-info' :
                            log.type === 'delete' ? 'bi-trash text-danger' :
                            'bi-person-check text-warning'
                          }`}></i>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <strong>{log.user}</strong>
                            <p className="mb-1">{log.action}</p>
                            <small className="text-muted">
                              <i className="bi bi-clock me-1"></i>
                              {log.time}
                            </small>
                          </div>
                          <button className="btn btn-sm btn-outline-secondary">
                            <i className="bi bi-eye"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="text-center mt-3">
                  <button className="btn btn-outline-primary btn-sm">
                    Load More
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="row g-3 mt-4">
        <div className="col-12 col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Total Roles</h6>
                  <h3 className="mb-0">{roles.length}</h3>
                </div>
                <div className="bg-primary bg-opacity-10 p-3 rounded">
                  <i className="bi bi-people fs-4 text-primary"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-12 col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Total Users</h6>
                  <h3 className="mb-0">{roles.reduce((acc, role) => acc + role.users, 0)}</h3>
                </div>
                <div className="bg-success bg-opacity-10 p-3 rounded">
                  <i className="bi bi-person-check fs-4 text-success"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-12 col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Permissions</h6>
                  <h3 className="mb-0">{permissions.reduce((acc, cat) => acc + cat.items.length, 0)}</h3>
                </div>
                <div className="bg-info bg-opacity-10 p-3 rounded">
                  <i className="bi bi-key fs-4 text-info"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-12 col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Recent Changes</h6>
                  <h3 className="mb-0">{auditLog.length}</h3>
                </div>
                <div className="bg-warning bg-opacity-10 p-3 rounded">
                  <i className="bi bi-clock-history fs-4 text-warning"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolesAndPermissions;
